import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { SearchResult } from '../services/api';
import { useBookmarks } from '../hooks/useBookmarks';
import { useAuth } from '../context/AuthContext';

interface SearchResultsProps {
  results: SearchResult[];
  isLoading: boolean;
  onResultClick: (result: SearchResult) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
}

export default function SearchResults({
  results,
  onResultClick,
  onLoadMore,
  hasMore,
  isLoadingMore,
}: SearchResultsProps) {
  const { isAuthenticated } = useAuth();
  const { isBookmarked, toggleBookmark, checkBookmarks } = useBookmarks();
  const { t } = useTranslation();

  // Check which judgments are bookmarked when results change
  useEffect(() => {
    if (isAuthenticated && results.length > 0) {
      const judgmentIds = results.map((r) => r.id);
      checkBookmarks(judgmentIds);
    }
  }, [isAuthenticated, results, checkBookmarks]);

  const courtTypeLabel: Record<string, string> = {
    SUPREME: t('courtType.supreme'),
    COMMON: t('courtType.common'),
    APPEAL: t('courtType.appeal'),
    REGIONAL: t('courtType.regional'),
    DISTRICT: t('courtType.district'),
    CONSTITUTIONAL_TRIBUNAL: t('courtType.constitutionalTribunal'),
    ADMINISTRATIVE: t('courtType.administrative'),
    NATIONAL_APPEAL_CHAMBER: t('courtType.nationalAppealChamber'),
  };

  const courtTypeClass: Record<string, string> = {
    SUPREME: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300',
    COMMON: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
    APPEAL: 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300',
    REGIONAL: 'bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300',
    DISTRICT: 'bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300',
    CONSTITUTIONAL_TRIBUNAL: 'bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300',
    ADMINISTRATIVE: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300',
    NATIONAL_APPEAL_CHAMBER: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300',
  };

  return (
    <div>
      {/* Results list */}
      <div className="space-y-4">
        {results.map((result) => (
          <div
            key={result.id}
            onClick={() => onResultClick(result)}
            className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
          >
            {/* Header */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
              <span
                className={`px-2 py-1 text-xs font-medium rounded ${
                  courtTypeClass[result.courtType] || 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300'
                }`}
              >
                {courtTypeLabel[result.courtType] || result.courtType}
              </span>
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                {formatCaseNumber(result.caseNumber)}
              </span>
              <span className="text-sm text-slate-400 dark:text-slate-500">
                {formatDate(result.judgmentDate)}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <SimilarityBadge similarity={result.similarity} />
                {isAuthenticated && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleBookmark(result.id);
                    }}
                    className={`p-1.5 rounded-lg transition-colors ${
                      isBookmarked(result.id)
                        ? 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                        : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                    title={isBookmarked(result.id) ? t('search.removeBookmark') : t('search.addBookmark')}
                  >
                    <svg
                      className="w-5 h-5"
                      fill={isBookmarked(result.id) ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Snippet */}
            <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-3">
              {cleanSnippet(result.snippet)}
            </p>

            {/* Footer */}
            <div className="mt-3 flex items-center gap-2">
              {result.chunkType && (
                <span className="text-xs text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">
                  {chunkTypeLabel(result.chunkType, t)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* No results */}
      {results.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-slate-500 dark:text-slate-400">
            {t('search.noResults')}
          </p>
        </div>
      )}

      {/* Load More button */}
      {hasMore && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 dark:disabled:bg-slate-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            {isLoadingMore ? (
              <>
                <svg
                  className="animate-spin w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>{t('search.loadingMore')}</span>
              </>
            ) : (
              <span>{t('search.loadMore')}</span>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

function SimilarityBadge({ similarity }: { similarity: number }) {
  const percentage = Math.round(similarity * 100);
  const { t } = useTranslation();
  let colorClass = 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400';

  if (percentage >= 70) {
    colorClass = 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300';
  } else if (percentage >= 50) {
    colorClass = 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300';
  } else if (percentage >= 30) {
    colorClass = 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300';
  }

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${colorClass}`}>
      {percentage}% {t('search.relevance')}
    </span>
  );
}

function formatDate(dateString: string): string {
  if (!dateString || dateString === 'Unknown') return '';
  try {
    const date = new Date(dateString);
    const locale = i18next.language === 'pl' ? 'pl-PL' : 'en-US';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

function formatCaseNumber(caseNumber: string | unknown): string {
  if (!caseNumber) return '';
  if (typeof caseNumber === 'string') {
    // Try to parse JSON if it looks like JSON array
    if (caseNumber.startsWith('[')) {
      try {
        const parsed = JSON.parse(caseNumber);
        if (Array.isArray(parsed)) {
          return parsed.map((c) => c.caseNumber || c).join(', ');
        }
      } catch {
        return caseNumber;
      }
    }
    return caseNumber;
  }
  if (Array.isArray(caseNumber)) {
    return caseNumber.map((c) => (typeof c === 'object' ? c.caseNumber : c)).join(', ');
  }
  return String(caseNumber);
}

function cleanSnippet(snippet: string): string {
  if (!snippet) return '';
  // Remove HTML tags
  return snippet.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function chunkTypeLabel(type: string, t: (key: string) => string): string {
  const labels: Record<string, string> = {
    reasoning: t('chunkType.reasoning'),
    decision: t('chunkType.decision'),
    facts: t('chunkType.facts'),
    header: t('chunkType.header'),
    full: t('chunkType.full'),
  };
  return labels[type] || type;
}
